'use client';
import React, { useState, useEffect, useRef } from 'react';
import Button from '../ui/Button';
import Icon from '../ui/FontAwesomeIcon';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import Image from 'next/image';
import MobileMenu from './MobileMenu';

interface Service {
  id: number;
  image: string;
  title: string;
  slug: string;
  description: string;
  borderRadius?: string;
}

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const [currentHash, setCurrentHash] = useState('');
  const [scrolled, setScrolled] = useState(false)
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    const updateHash = () => {
      setCurrentHash(window.location.hash || '')
    }

    const updateScroll = () => {
      setScrolled(window.scrollY > 10)
    }

    updateHash()
    updateScroll()

    window.addEventListener('hashchange', updateHash)
    window.addEventListener('scroll', updateScroll)

    return () => {
      window.removeEventListener('hashchange', updateHash)
      window.removeEventListener('scroll', updateScroll)
    }
  }, []);

  const menuItems = [
    { text: 'Home', href: '/', color: 'text-text-primary' },
    { text: 'Layanan SEO', href: '/layanan', color: 'text-text-primary' },
    { text: 'Tentang Kami', href: '/tentang-kami', color: 'text-text-primary' },
    { text: 'Klien Kami', href: '/klien', color: 'text-text-primary' },
    { text: 'Kontak', href: '/kontak-kami', color: 'text-text-primary' },
    { text: 'Blog', href: '/blog', color: 'text-text-primary' },
  ];

  const fetchServices = async () => {
    if (mountedRef.current) {
      setLoading(true);
    }

    try {
      const res = await fetch('/api/services?menu=1&limit=4');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const arr = Array.isArray(data) ? data : [];
      if (mountedRef.current) setServices(arr);
    } catch (err: any) {
      if (mountedRef.current) {
        setServices([]);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchServices();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return (
    <header data-component-id="src\components\common\Header.tsx:89:4" data-component-path="src\components\common\Header.tsx" data-component-line="89" data-component-end-line="236" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D" className={`w-full sticky top-0 z-50 transition-colors duration-300
        ${scrolled ? 'bg-white shadow-md' : 'bg-[#baceeb]'}
      `}>
      <div data-component-id="src\components\common\Header.tsx:92:6" data-component-path="src\components\common\Header.tsx" data-component-line="92" data-component-end-line="228" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-%5B1440px%5D%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div data-component-id="src\components\common\Header.tsx:93:8" data-component-path="src\components\common\Header.tsx" data-component-line="93" data-component-end-line="227" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20py-4%20md%3Apy-8%22%7D" className="flex items-center justify-between py-4 md:py-8">
          {/* Logo and Navigation Container */}
          <div data-component-id="src\components\common\Header.tsx:95:10" data-component-path="src\components\common\Header.tsx" data-component-line="95" data-component-end-line="194" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-start%20flex-1%22%7D" className="flex items-center justify-start flex-1">
            <div data-component-id="src\components\common\Header.tsx:96:12" data-component-path="src\components\common\Header.tsx" data-component-line="96" data-component-end-line="193" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-6%20sm%3Agap-8%20md%3Agap-9%20w-full%22%7D" className="flex items-center gap-6 sm:gap-8 md:gap-9 w-full">
              {/* Logo */}
              <div data-component-id="src\components\common\Header.tsx:98:14" data-component-path="src\components\common\Header.tsx" data-component-line="98" data-component-end-line="113" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-end%20px-4%20sm%3Apx-6%20md%3Apx-%5B26px%5D%20flex-shrink-0%22%7D" className="flex items-center justify-end px-4 sm:px-6 md:px-[26px] flex-shrink-0">
                <Link data-component-id="src\components\common\Header.tsx:99:16" data-component-path="src\components\common\Header.tsx" data-component-line="99" data-component-end-line="109" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%7D" href="/">
                  <Image data-component-id="src\components\common\Header.tsx:100:18" data-component-path="src\components\common\Header.tsx" data-component-line="100" data-component-end-line="108" data-component-file="Header.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Flogo.png%22%2C%22alt%22%3A%22AhliSEO%20Logo%22%2C%22className%22%3A%22h-8%20w-auto%22%7D"
                    src="/images/logo.png"
                    alt="AhliSEO Logo"
                    width={128}
                    height={32}
                    quality={100}
                    className="h-8 w-auto"
                    priority
                  />
                </Link>
                {/* <h5 className="text-lg sm:text-xl md:text-3xl font-[Sora] font-semibold leading-loose text-text-primary">
                  AhliSEO
                </h5> */}
              </div>

              {/* Desktop Navigation */}
              <nav data-component-id="src\components\common\Header.tsx:116:14" data-component-path="src\components\common\Header.tsx" data-component-line="116" data-component-end-line="192" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20lg%3Ablock%20w-full%20lg%3Aflex-1%22%7D" className="hidden lg:block w-full lg:flex-1">
                <div data-component-id="src\components\common\Header.tsx:117:16" data-component-path="src\components\common\Header.tsx" data-component-line="117" data-component-end-line="191" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20items-center%20justify-center%20gap-4%20lg%3Agap-8%20bg-transparent%20p-0%22%7D" className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-8 bg-transparent p-0">
                  {menuItems?.map((item, index) => {
                    const isActive = (() => {
                      if (!item?.href) return false;

                      // If the link contains a hash (anchor), handle href formats like:
                      // '#contact', '/#contact', '/services#pricing', '/services#'
                      if (item.href.includes('#')) {
                        const [pathPart, hashPart] = item.href.split('#');
                        const linkHash = hashPart ? `#${hashPart}` : '';
                        const linkPath = pathPart && pathPart !== '' ? pathPart : '/';

                        const pathMatches = linkPath === '/' ? pathname === '/' : (pathname || '').startsWith(linkPath);
                        return pathMatches && linkHash !== '' && currentHash === linkHash;
                      }

                      // Exact root match
                      if (item.href === '/') return pathname === '/';

                      // Default: consider a prefix match for path-based menu items
                      return (pathname || '').startsWith(item.href || '');
                    })();

                    // if href = /layanan create dropdown and loop services
                    if (item?.href === '/layanan') {
                      return (
                        <span data-component-id="src\components\common\Header.tsx:143:24" data-component-path="src\components\common\Header.tsx" data-component-line="143" data-component-end-line="177" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22w-full%20lg%3Aw-auto%20relative%20group%22%7D" key={item.href} className="w-full lg:w-auto relative group">
                          {/* Trigger */}
                          <button data-component-id="src\components\common\Header.tsx:145:26" data-component-path="src\components\common\Header.tsx" data-component-line="145" data-component-end-line="152" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%7D"
                            type="button"
                            className={`w-full text-left text-base sm:text-lg font-plusjakarta font-bold leading-relaxed
                              ${isActive ? 'text-primary-accent' : item.color}
                              hover:text-primary-accent transition-colors duration-200 flex items-center gap-1`}
                          >
                            {item.text}
                          </button>

                          {/* Submenu for Desktop - hover based usually, or click */}
                          <div data-component-id="src\components\common\Header.tsx:155:26" data-component-path="src\components\common\Header.tsx" data-component-line="155" data-component-end-line="176" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20hidden%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20group-hover%3Ablock%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20absolute%20top-full%20left-0%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20w-%5B260px%5D%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20z-50%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20pt-2%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%22%7D"
                            className={`
                              hidden
                              group-hover:block
                              absolute top-full left-0
                              w-[260px]
                              z-50
                              pt-2
                            `}
                          >
                            <div data-component-id="src\components\common\Header.tsx:165:28" data-component-path="src\components\common\Header.tsx" data-component-line="165" data-component-end-line="175" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-white%20shadow-lg%20rounded-lg%20py-3%20px-4%20flex%20flex-col%20gap-2%20border%20border-gray-100%22%7D" className="bg-white shadow-lg rounded-lg py-3 px-4 flex flex-col gap-2 border border-gray-100">
                              {services.map(service => (
                                <Link data-component-id="src\components\common\Header.tsx:167:32" data-component-path="src\components\common\Header.tsx" data-component-line="167" data-component-end-line="173" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22block%20pl-3%20py-2%20text-sm%20font-plusjakarta%20font-medium%20text-text-primary%20hover%3Atext-primary-accent%20hover%3Abg-gray-50%20rounded-md%20transition-colors%22%7D"
                                  key={service.id}
                                  href={`/layanan/${service.slug}`}
                                  className="block pl-3 py-2 text-sm font-plusjakarta font-medium text-text-primary hover:text-primary-accent hover:bg-gray-50 rounded-md transition-colors"
                                >
                                  {service.title}
                                </Link>
                              ))}
                            </div>
                          </div>
                        </span>
                      )
                    }

                    return (
                      <Link data-component-id="src\components\common\Header.tsx:182:22" data-component-path="src\components\common\Header.tsx" data-component-line="182" data-component-end-line="188" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                        key={index}
                        href={item?.href}
                        className={`text-base sm:text-lg font-plusjakarta font-bold leading-relaxed ${isActive ? 'text-primary-accent' : item?.color} hover:text-primary-accent transition-colors duration-200 whitespace-nowrap`}
                      >
                        {item?.text}
                      </Link>
                    );
                  })}
                </div>
              </nav>
            </div>
          </div>

          {/* Hamburger Menu Icon (Mobile only) */}
          <button data-component-id="src\components\common\Header.tsx:197:10" data-component-path="src\components\common\Header.tsx" data-component-line="197" data-component-end-line="207" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22block%20lg%3Ahidden%20p-2%20ml-4%20text-text-primary%20hover%3Atext-primary-accent%20transition-colors%22%7D"
            className="block lg:hidden p-2 ml-4 text-text-primary hover:text-primary-accent transition-colors"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen(true)}
          >
            <Icon data-component-id="src\components\common\Header.tsx:202:12" data-component-path="src\components\common\Header.tsx" data-component-line="202" data-component-end-line="206" data-component-file="Header.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
              icon={faBars}
              size="lg"
              color="text-text-primary"
            />
          </button>

          {/* CTA Button */}
          {/* <div className="hidden lg:block ml-14">
            <Button
              text="Hubungi Kami"
              text_font_size="text-lg"
              text_font_family="Plus Jakarta Sans"
              text_font_weight="font-bold"
              text_line_height="leading-relaxed"
              text_color="text-text-white"
              fill_background_color="bg-primary-success"
              border_border_radius="rounded-sm"
              padding="py-2 px-5"
              layout_gap="6px"
              leftIcon={faWhatsapp}
              leftIconColor="text-white"
              className="whitespace-nowrap"
            />
          </div> */}
        </div>
      </div>

      {/* Search Overlay / Mobile Menu */}
      <MobileMenu data-component-id="src\components\common\Header.tsx:231:6" data-component-path="src\components\common\Header.tsx" data-component-line="231" data-component-end-line="235" data-component-file="Header.tsx" data-component-name="MobileMenu" data-component-content="%7B%22elementName%22%3A%22MobileMenu%22%7D"
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        services={services}
      />
    </header>
  );
};

export default Header;